import React, { useState } from "react";
import { CommonSection } from "../../components/common/CommonSection";
import BreadcrumbNavigation from "../../components/common/BreadcrumbNavigation";
import { CCard } from "../../components/common/CCard";
import { Outlet, useLocation } from "react-router";
import AccountSideMenu from "../../components/common/AccountSideMenu";
import { useAuth } from "../../hooks/AuthContext";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router";
import { auth } from "../../../firebase";
import { toast } from "react-toastify";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Drawer from "@mui/material/Drawer";
import Grid from "@mui/material/Grid2";
import { Box, Chip, Stack, Typography } from "@mui/material";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";

function kebabToTitle(str) {
  return str
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}
function Account() {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [drawerOpen, setDrawerOpen] = useState(false);
  let parts = location?.pathname.split("/");
  const currentMenu = kebabToTitle(parts?.[2]);
  const logoutHandler = async () => {
    try {
      await signOut(auth);
      localStorage.clear();
      logout();
      toast.warning("Logged out successfully");
      navigate("/");
    } catch (error) {
      toast.error("Error logging out");
    }
  };

  return (
    <>
      <CommonSection
        style={{
          paddingTop: "32px !important",
          background: "var(--neutral-100)",
        }}
      >
        <BreadcrumbNavigation
          items={[
            { label: "Account", path: "/account/profile" },
            { label: `${currentMenu}` },
          ]}
        />

        <AccountHero>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: "24px", md: "30px" },
              color: "#2B241A",
              lineHeight: "130%",
              mb: 0.75,
            }}
          >
            Account Hub
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              color: "#6E624B",
              maxWidth: 620,
              mb: 2,
            }}
          >
            Manage profile details, addresses, and order activity from one place.
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <HeroBadge icon={<PersonOutlineRoundedIcon sx={{ fontSize: 16 }} />} label="Profile controls" />
            <HeroBadge icon={<ReceiptLongOutlinedIcon sx={{ fontSize: 16 }} />} label="Order tracking" />
          </Stack>
        </AccountHero>

        {isMobile && (
          <IconButton
            onClick={() => setDrawerOpen(true)}
            sx={{
              position: "fixed",
              top: 140,
              left: 10,
              background: "rgba(255,255,255,0.96)",
              border: "1px solid rgba(183, 124, 10, 0.24)",
              borderRadius: "14px",
              boxShadow: "0 10px 24px rgba(173, 132, 61, 0.12)",
              zIndex: 10,
              "&:hover": {
                background: "var(--neutral-white)",
              },
            }}
          >
            <MenuIcon sx={{ color: "var(--primary-red-base)" }} />
          </IconButton>
        )}

        <Grid container spacing={3} sx={{ mt: 4 }}>
          {!isMobile && (
            <Grid size={{ xs: 12, sm: 3 }}>
              <AccountSideMenu logoutHandler={logoutHandler} />
            </Grid>
          )}

          <Grid size={{ xs: 12, sm: 9 }}>
            <ContentShell>
              <Outlet />
            </ContentShell>
          </Grid>
        </Grid>

        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          sx={{ display: { sm: "block", md: "none" } }}
        >
          <AccountSideMenu
            logoutHandler={logoutHandler}
            onClose={() => setDrawerOpen(false)}
            isMobile={true}
          />
        </Drawer>
      </CommonSection>
    </>
  );
}

const AccountHero = styled(Box)(({ theme }) => ({
  border: "1px solid rgba(183, 124, 10, 0.24)",
  borderRadius: "18px",
  padding: theme.spacing(3),
  marginTop: theme.spacing(3),
  background:
    "radial-gradient(circle at top left, rgba(255,247,231,0.95) 0%, rgba(255,255,255,0.98) 48%, rgba(255,250,242,0.95) 100%)",
  boxShadow: "0 14px 30px rgba(173, 132, 61, 0.1)",
}));

const HeroBadge = styled(Chip)(() => ({
  borderRadius: "999px",
  background: "rgba(255, 246, 225, 0.95)",
  border: "1px solid rgba(188, 128, 15, 0.24)",
  color: "#6B5425",
  fontWeight: 600,
}));

const ContentShell = styled(CCard)(({ theme }) => ({
  padding: theme.spacing(3),
  border: "1px solid rgba(183, 124, 10, 0.22)",
  borderRadius: "18px",
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(255,250,242,0.96) 100%)",
  boxShadow: "0 10px 24px rgba(173, 132, 61, 0.08)",
}));

export default Account;
